/**
 * BlockUserButton — the "Bloquear" affordance + confirmation modal.
 *
 * Drop into any screen that exposes another user (chat header, vehicle
 * detail host card, public profile). Renders nothing visible by default
 * — wraps a child Pressable trigger that opens the confirm sheet.
 *
 * After a successful block, calls the optional `onBlocked` callback so
 * the parent screen can dismiss / route away (since the content is
 * about to disappear from view via RLS anyway).
 */
import { useCallback, useState } from 'react'
import { View, Text, TextInput as RNTextInput, Platform } from 'react-native'
import { AppModal } from './AppModal'
import { GlassSheet } from './GlassSheet'
import { useTranslation } from 'react-i18next'
import { Pressable } from './Pressable'
import { TrackedNativePressable as RNPressable } from './TrackedNativePressable'
import { useTheme } from '../../lib/hooks/use-theme'
import { useSemanticColors, SPACING, RADIUS, INTERACTION } from '../../lib/theme/tokens'
import { COLORS, FONTS } from '../../lib/brand'
import { useBlockUser } from '../../lib/hooks/use-user-blocks'
import { impact, ImpactStyle } from '@/lib/utils/haptics'
import { toast } from '../../lib/toast'
import { errorMessage } from '../../lib/utils/observability'

interface BlockUserButtonProps {
  /** ID of the user being blocked (the OTHER party). */
  targetId: string
  /** Display name shown in the confirm copy. */
  targetName: string
  /** Renderer for the trigger element (typically a row in an overflow menu). */
  children: (open: () => void) => React.ReactNode
  /** Called after successful block — use to pop/replace the parent screen. */
  onBlocked?: () => void
}

export function BlockUserButton({
  targetId,
  targetName,
  children,
  onBlocked,
}: BlockUserButtonProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const blockMutation = useBlockUser()

  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')

  const handleClose = useCallback(() => {
    if (blockMutation.isPending) return
    setOpen(false)
    setReason('')
  }, [blockMutation.isPending])

  const handleConfirm = useCallback(async () => {
    // Blocking another user is irreversible from this screen. Acknowledge the
    // confirm tap immediately instead of only on the mutation's result.
    impact(ImpactStyle.Heavy)
    try {
      await blockMutation.mutateAsync({
        blockedId: targetId,
        reason: reason.trim() || undefined,
      })
      setOpen(false)
      setReason('')
      toast.success(t('block.success_toast'))
      onBlocked?.()
    } catch (err) {
      toast.error(errorMessage(err, t('block.error_generic')))
    }
  }, [blockMutation, targetId, reason, t, onBlocked])

  return (
    <>
      {children(() => setOpen(true))}

      <AppModal visible={open} transparent animationType="fade" onRequestClose={handleClose}>
        {/*
          EL FONDO ES UN HERMANO DE LA TARJETA, NO SU ENVOLTORIO.

          HISTORIA, PORQUE LAS DOS VERSIONES ANTERIORES FALLARON DISTINTO:

          (1) Un fondo `components/ui/Pressable` envolviendo un `View` que
              intentaba reclamar el toque con `onStartShouldSetResponder`. Esa
              guarda no aguanto: manejado en un navegador, CADA toque dentro de
              la tarjeta cerraba el dialogo --medido al 8%, 35% y 60% de su
              alto, y sobre el campo de motivo-- asi que el motivo no se podia
              escribir nunca.

          (2) Se anidaron dos RNPressable para que el de adentro ganara el
              responder y el toque muriera ahi. Funciono, y produjo HTML
              invalido: en web `accessibilityRole="button"` renderiza `<button>`,
              y el fondo quedo siendo un boton que CONTIENE los botones de
              Cancelar y Bloquear. React lo dijo con todas sus letras --"In HTML,
              <button> cannot be a descendant of <button>. This will cause a
              hydration error"-- y lo encontro la suite E2E el 2026-08-25, en
              cuanto los siete specs pasaron a compartir un solo filtro de ruido.
              Un boton de pantalla completa etiquetado "Cancelar" que envuelve al
              dialogo tampoco se anuncia bien: no es solo invalido, es confuso.

          (3) Esto. Es el patron que `FilterSheet` ya usaba: el fondo es un
              hermano ABSOLUTO Y VACIO, y la tarjeta va al lado. Sin anidamiento
              no hay nada que arbitrar --un toque en la tarjeta jamas alcanza al
              fondo, por construccion-- ni HTML invalido que producir. La tarjeta
              vuelve a ser un `View`, porque nunca fue un control: consumir el
              toque dejo de ser trabajo de nadie.

          Lo verifica `e2e/web/overlay-dismissal.spec.ts`, que toca la tarjeta a
          tres alturas y escribe en el campo. La ventana nativa aparte de Android
          no se puede ejercitar en un navegador; eso descansa en el precedente de
          FilterSheet, no en la prueba.
        */}
        <View
          style={{
            flex: 1,
            justifyContent: 'center',
            paddingHorizontal: SPACING.lg,
          }}
        >
          <RNPressable
            onPress={handleClose}
            accessibilityRole="button"
            accessibilityLabel={t('common.cancel')}
            style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}
          />
          <GlassSheet
            testID="block.dialog"
            accessibilityViewIsModal
            accessibilityLabel={t('block.confirm_title', { name: targetName })}
            style={{
              borderRadius: RADIUS.lg,
            }}
            contentStyle={{
              padding: SPACING.lg,
              gap: SPACING.md,
            }}
          >
            <Text
              style={{
                fontSize: 20,
                fontFamily: FONTS.bodyBold,
                color: colors.textPrimary,
                letterSpacing: -0.3,
              }}
            >
              {t('block.confirm_title', { name: targetName })}
            </Text>
            <Text
              style={{
                fontSize: 14,
                lineHeight: 20,
                fontFamily: FONTS.body,
                color: colors.textSecondary,
              }}
            >
              {t('block.confirm_body')}
            </Text>

            <View>
              <Text
                style={{
                  fontSize: 12,
                  fontFamily: FONTS.body,
                  color: colors.textMuted,
                  marginBottom: SPACING.xs,
                  letterSpacing: 0.2,
                  textTransform: 'uppercase',
                }}
              >
                {t('block.reason_label')}
              </Text>
              <RNTextInput
                value={reason}
                onChangeText={setReason}
                multiline
                numberOfLines={2}
                maxLength={300}
                placeholderTextColor={colors.textMuted}
                style={[
                  {
                    minHeight: INTERACTION.fieldHeight,
                    paddingHorizontal: SPACING.md,
                    paddingVertical: SPACING.md,
                    borderRadius: RADIUS.md,
                    borderWidth: 1,
                    borderColor: colors.border,
                    backgroundColor: colors.surfaceSubtle,
                    fontFamily: FONTS.body,
                    fontSize: 15,
                    color: colors.textPrimary,
                    textAlignVertical: 'top',
                  },
                  Platform.OS === 'web'
                    ? ({ outlineStyle: 'none' } as Record<string, string>)
                    : null,
                ]}
              />
            </View>

            <View style={{ flexDirection: 'row', gap: SPACING.sm, marginTop: SPACING.xs }}>
              <Pressable
                onPress={handleClose}
                disabled={blockMutation.isPending}
                style={({ pressed }) => ({
                  flex: 1,
                  height: 48,
                  borderRadius: RADIUS.md,
                  backgroundColor: colors.surfaceSubtle,
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: pressed ? 0.7 : 1,
                })}
                accessibilityRole="button"
                accessibilityLabel={t('common.cancel')}
                testID="block.cancel"
              >
                <Text
                  style={{
                    fontFamily: FONTS.body,
                    fontSize: 15,
                    color: colors.textPrimary,
                  }}
                >
                  {t('common.cancel')}
                </Text>
              </Pressable>
              <Pressable
                onPress={handleConfirm}
                disabled={blockMutation.isPending}
                style={({ pressed }) => ({
                  flex: 1,
                  height: 48,
                  borderRadius: RADIUS.md,
                  backgroundColor: COLORS.coral.dark,
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: pressed || blockMutation.isPending ? 0.7 : 1,
                })}
                accessibilityRole="button"
                accessibilityLabel={t('block.action')}
                testID="block.confirm"
              >
                <Text
                  style={{
                    fontFamily: FONTS.bodyBold,
                    fontSize: 15,
                    color: '#FFFFFF',
                  }}
                >
                  {blockMutation.isPending ? '…' : t('block.action')}
                </Text>
              </Pressable>
            </View>
          </GlassSheet>
        </View>
      </AppModal>
    </>
  )
}
